import {BehaviorSubject} from 'rxjs';
import {DataService} from '../data/data.service';
import {Injectable} from '@angular/core';
import {Utils} from '../../utils/utils.class';
import {FilterSet} from '../../interfaces/filter-set.interface';
import {FilterSetOption} from '../../interfaces/filter-set-options.interface';
import {FilterOutput} from '../../interfaces/filter-outpt.interface';
import {FiltersConfig} from '../../interfaces/filters-config.interface';
import {FilterInput} from '../../interfaces/filter-input.interface';
import {isDictionaryItem} from '../../interfaces/dictionary-item.interface';
import * as dot from 'dot-prop';
import { FilterOutputWrapper } from '../../interfaces/filter-outpt-wrapper.interface';

@Injectable()
export class FiltersService {
  private readonly iterableTypes = ['autocomplete', 'select', 'multi-select'];
  private readonly baseStorageKey = 'filters';

  public readonly defaultIcon = '';
  public filterSetSubject$ = new BehaviorSubject<FilterSet>(null);

  constructor(private dataService: DataService) {}

  public getFilters(setName: string): Promise<FilterSet> {
    return this.dataService.getObject(this.baseStorageKey + '_' + setName).catch((error) => {
      if (error.reason !== 'missing') {
        throw error;
      }
      return  {
        filterConfig: {
          label: this.defaultIcon,
          filters: [],
        },
        filters: [],
        name: setName,
        options: {},
      };
    });
  }

  private saveEmptyFilters(setName: string): Promise<FilterSet> {
    return this.saveFilters(setName, {
      filterConfig: {
        label: this.defaultIcon,
        filters: [],
      },
      filters: [],
      name: setName,
      options: {},
    });
  }

  public applyFilters(setName: string, filters: FilterOutput[] | FilterOutputWrapper, filterFunction?: (o: object) => boolean): Promise<FilterSet> {
    return this.getFilters(setName).then((filterSet: FilterSet) => {
      const previousFilterSet: FilterSet = this.filterSetSubject$.value;
      const newFilterSet: FilterSet = Array.isArray(filters) ? { ...filterSet, filters} : { ...filterSet, ...filters};

      if (filterFunction) {
        newFilterSet.filterFunction = filterFunction;
      }

      const searchQuery = (previousFilterSet && previousFilterSet.searchQuery) || '';
      return this.saveFilters(setName, newFilterSet).then((newFilterSet) => {
        this.filterSetSubject$.next({ ...newFilterSet, name: setName, searchQuery: searchQuery });
        return newFilterSet;
      });
    });
  }

  public setConfig(setName: string, config: FiltersConfig): Promise<FilterSet> {
    return this.getFilters(setName).then((filters: FilterSet) => {
      filters.filterConfig = config;
      return this.saveFilters(setName, filters);
    });
  }

  public extractOptionsFromData(setName: string, data: Array<object>): Promise<FilterSet> {
    return this.getFilters(setName).then((filterSet: FilterSet) => {
      const filters: Array<FilterInput> = filterSet.filterConfig.filters;

      filters.forEach((filter: FilterInput) => {
        if (this.iterableTypes.indexOf(filter.inputType) > -1) {
          filterSet.options[filter.column] = [];
          data.forEach((elem) => {
            this.addOptions(filter.column, filter.valueName, filter.valueKey, elem, filterSet);
          });
        }
      });

      return this.saveFilters(setName, filterSet);
    });
  }

  private addOptions(paramName: string, paramValueName: string, paramValueKey: string, elem: object, filterSet: FilterSet): void {
    const valueByParamName = dot.get(elem, paramName);
    if (!valueByParamName) {
      return;
    }
    if (Array.isArray(valueByParamName)) {
      valueByParamName.forEach((option) => {
        this.addSingleOption(paramValueName, paramValueKey, option, filterSet.options[paramName]);
      });
    } else {
      this.addSingleOption(paramValueName, paramName, elem, filterSet.options[paramName]);
    }
  }

  private addSingleOption(name: string, value: string, option: object, optionsArray: Array<object>): void {
    const newOption: FilterSetOption = {
      label: Utils.evaluateString(name, option),
      value: this.getElementValue(option, value),
    };

    const regex = /^(null\s*)+$/gm;

    if (!regex.test(newOption.label) && !Utils.checkIfArrayContains(optionsArray, newOption)) {
      optionsArray.push(newOption);
    }
  }

  private getElementValue(element: object, parameterName: string): string | number | object | boolean {
    const params = parameterName.split('.');
    return this.getElementValueByParams(element, params);
  }

  private getElementValueByParams(element: object, params: Array<string>): string | number | object | boolean {
    if (params.length > 1) {
      return this.getElementValueByParams(element[params[0]], params.splice(1));
    }
    return element[params[0]];
  }

  private saveFilters(setName: string, filters: FilterSet): Promise<FilterSet> {
    return this.dataService.saveObject<FilterSet>(this.baseStorageKey + '_' + setName, filters).then(() => filters);
  }

  public getOptions(setName: string, paramName: string): Promise<Array<FilterSetOption>> {
    return this.getFilters(setName).then((filterSet: FilterSet) => {
      return filterSet ? filterSet.options[paramName] : [];
    });
  }

  clearFilters(setName: string) {
    return this.saveEmptyFilters(setName);
  }

  async applySearch(setName: string, query: string) {
    let previousFilterSet = this.filterSetSubject$.value;
    if(!previousFilterSet) {
      previousFilterSet = await this.getFilters(setName);
    }
    previousFilterSet.searchQuery = query;
    return this.saveFilters(setName, previousFilterSet).then((newFilterSet) => {
      this.filterSetSubject$.next(newFilterSet);
      return newFilterSet;
    });
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/services/filters/filters.service.ts